import { Col, Input, List, Row } from "@administrate/piston-ux";
import { FormArray } from "@administrate/piston-ux/lib/Form";
import { observer } from "mobx-react-lite";
import { TFunction, useTranslation } from "react-i18next";
import {
  Contact,
  PointOfSaleLearnerFieldDefinition,
} from "../../generated/weblinkTypes";
import { mapPreExistingContactValues } from "../../utils/bookingHelpers";
import { getUniqueId } from "../../utils/displayHelpers";
import { LearnerRow } from "./LearnerRow";
import { SelectedContact, UNNAMED_LEARNER_CONTACT } from "./selectedContact";

export const GLOBAL_LEARNER_LIMIT = 100;

export type LearnerType = {
  id: string;
  learnerNumber: number;
  contact: SelectedContact;
  posFieldValues: Record<string, unknown>;
};
export interface LearnersFormValues {
  learnerNumber: number;
  learners: LearnerType[];
  learnerLength: number;
}

export interface MultipleLearnerSelectorProps {
  contacts(inputValue: string): Promise<Contact[]>;
  values: LearnersFormValues;
  maxLearners?: number | null;
  pointOfSaleFields: PointOfSaleLearnerFieldDefinition[];
  getMaxLearnersReachedMessages?: (
    maxLearners: number,
    t?: TFunction<"translation", undefined>,
  ) => string[];
  disabled?: boolean;
}

const DEFAULT_MAX_LEARNER_MESSAGE_FN = (
  maxLearners: number,
  t: TFunction<"translation", undefined>,
): string[] => {
  return [`${t("availableLearnerSpaces")}: ${maxLearners}`];
};

export const MultipleLearnerSelector = observer<MultipleLearnerSelectorProps>(
  ({
    contacts,
    values,
    maxLearners,
    pointOfSaleFields,
    getMaxLearnersReachedMessages,
    disabled = false,
  }) => {
    const { t } = useTranslation();

    const handleChangeLearnerNumber = (value: string) => {
      const newLearnerLength = parseInt(value);
      if (
        (maxLearners && newLearnerLength > maxLearners) ||
        newLearnerLength > GLOBAL_LEARNER_LIMIT
      ) {
        return;
      }
      if (newLearnerLength >= 0) {
        values.learnerLength = newLearnerLength;
        if (newLearnerLength < values.learners.length) {
          values.learners = values.learners.slice(0, newLearnerLength);
        } else if (newLearnerLength > values.learners.length) {
          const unnamedLearners = [];
          for (let i = values.learners.length; i < newLearnerLength; i++) {
            const unnamedLearner: LearnerType = {
              id: "unnamed",
              learnerNumber: i,
              contact: UNNAMED_LEARNER_CONTACT,
              posFieldValues: {},
            };

            if (pointOfSaleFields) {
              pointOfSaleFields.forEach(field => {
                unnamedLearner.posFieldValues[field.key] = null;
              });
            }
            unnamedLearners.push(unnamedLearner);
          }
          values.learners = [...values.learners, ...unnamedLearners];
        }
      }
    };
    const maxLearnersExceeded = (v: number) => {
      return (
        maxLearners !== null && maxLearners !== undefined && v > maxLearners
      );
    };

    const handleSelectContact = (
      newChoice: Contact,
      _: Contact,
      index: number,
    ) => {
      if (newChoice !== null) {
        const currentLearner = values.learners[index];
        if (!currentLearner) {
          return;
        }

        currentLearner.id = newChoice.id;
        currentLearner.learnerNumber = index;

        mapPreExistingContactValues(
          pointOfSaleFields,
          currentLearner,
          newChoice,
        );
      } else {
        values.learners[index] = {
          id: UNNAMED_LEARNER_CONTACT.id,
          learnerNumber: index,
          contact: UNNAMED_LEARNER_CONTACT,
          posFieldValues: {},
        };
      }
    };

    return (
      <>
        {!disabled && (
          <>
            <Row>
              <Col xs={6} sm={4}>
                <Input
                  type="number"
                  label={t("numberOfLearners")}
                  name="learnerNumber"
                  autoFocus
                  onChange={handleChangeLearnerNumber}
                  valid={v => {
                    const numericValue = parseInt(v);
                    return (
                      numericValue > 0 &&
                      !maxLearnersExceeded(numericValue) &&
                      numericValue <= GLOBAL_LEARNER_LIMIT
                    );
                  }}
                  min={1}
                  max={maxLearners ? maxLearners : undefined}
                  uniqueId={getUniqueId()}
                />
              </Col>
            </Row>
            {maxLearners &&
              maxLearnersExceeded(values.learnerNumber) &&
              (getMaxLearnersReachedMessages || DEFAULT_MAX_LEARNER_MESSAGE_FN)(
                values.learnerNumber,
                t,
              ).map((message: string) => {
                return (
                  <Row>
                    <Col xs={12}>{message}</Col>
                  </Row>
                );
              })}
            {values.learnerNumber > GLOBAL_LEARNER_LIMIT && (
              <Row>
                <Col xs={12}>
                  {t("youCannotBookOver")} {GLOBAL_LEARNER_LIMIT}{" "}
                  {t("learnersUsingThisTool")}
                </Col>
              </Row>
            )}
            <hr />
          </>
        )}
        <List extraClass="learners">
          <FormArray name="learners">
            <LearnerRow
              contacts={contacts}
              onChange={handleSelectContact}
              posFields={pointOfSaleFields}
              disabled={disabled || maxLearnersExceeded(values.learnerNumber)}
            />
          </FormArray>
        </List>
      </>
    );
  },
);
